import { inject, Injectable } from '@angular/core';
import { SheetFormat } from '../models/sheet-format.model';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

export interface SheetFormatUpsertValues {
  name: string;
  product_code: string;
  manufacturer: string;
  page_width: number;
  page_height: number;
  label_width: number;
  label_height: number;
  columns: number;
  rows: number;
  margin_top: number;
  margin_left: number;
  gutter_horizontal: number;
  gutter_vertical: number;
  corner_radius: number;
}

export interface UpdateSheetFormatParams {
  id: string;
  values: SheetFormatUpsertValues;
}

type NumericSheetFormatKey = Exclude<
  keyof SheetFormatUpsertValues,
  'name' | 'product_code' | 'manufacturer'
>;

/**
 * Service for reading sheet format definitions from local SQLite.
 * Sheet formats define the physical layout of label sheets.
 * Read-only — formats are seeded as system data.
 */
@Injectable({
  providedIn: 'root',
})
export class SheetFormatService {
  private db = inject(DatabaseService);
  private logger = inject(LoggerService).getLogger('sheet-formats');

  // ---------------------------------------------------------------------------
  // Parsing
  // ---------------------------------------------------------------------------

  private parseRow(row: any): SheetFormat {
    return {
      id: row.id,
      name: row.name || '',
      product_code: row.product_code || '',
      manufacturer: row.manufacturer || '',
      page_width: row.page_width || 0,
      page_height: row.page_height || 0,
      label_width: row.label_width || 0,
      label_height: row.label_height || 0,
      columns: row.columns || 0,
      rows: row.rows || 0,
      margin_top: row.margin_top || 0,
      margin_left: row.margin_left || 0,
      gutter_horizontal: row.gutter_horizontal || 0,
      gutter_vertical: row.gutter_vertical || 0,
      corner_radius: row.corner_radius || 0,
      is_system: row.is_system ?? 1,
      created_by: row.created_by || 'system',
      created_at: row.created_at || 0,
    };
  }

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  /** Get all sheet formats, ordered by name. */
  async getAll(): Promise<SheetFormat[]> {
    await this.db.init();
    this.logger.debug('Getting all sheet formats...');
    const results = await this.db.exec('SELECT * FROM sheet_formats ORDER BY name');
    return results.map((row) => this.parseRow(row));
  }

  /** Get a single sheet format by ID. Returns null if not found. */
  async getById(id: string): Promise<SheetFormat | null> {
    await this.db.init();
    const results = await this.db.exec('SELECT * FROM sheet_formats WHERE id = ?', [id]);
    return results.length > 0 ? this.parseRow(results[0]) : null;
  }

  // ---------------------------------------------------------------------------
  // Reactive Queries (RQS)
  // ---------------------------------------------------------------------------

  /** Reactive query for all sheet formats. */
  createQuery(): QuerySignal<SheetFormat[]> {
    return this.db.createQuery('sheet_formats', () => this.getAll());
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  private validate(values: SheetFormatUpsertValues): void {
    const positive: NumericSheetFormatKey[] = [
      'page_width',
      'page_height',
      'label_width',
      'label_height',
    ];
    for (const key of positive) {
      if (!(values[key] > 0)) {
        throw new Error(`Invalid value for ${key}`);
      }
    }

    const nonNegative: NumericSheetFormatKey[] = [
      'margin_top',
      'margin_left',
      'gutter_horizontal',
      'gutter_vertical',
      'corner_radius',
    ];
    for (const key of nonNegative) {
      if (values[key] < 0) {
        throw new Error(`Invalid value for ${key}`);
      }
    }

    if (!Number.isInteger(values.columns) || values.columns < 1 || values.columns > 50) {
      throw new Error('Columns must be an integer between 1 and 50');
    }
    if (!Number.isInteger(values.rows) || values.rows < 1 || values.rows > 80) {
      throw new Error('Rows must be an integer between 1 and 80');
    }

    if (values.label_width > values.page_width || values.label_height > values.page_height) {
      throw new Error('Label size cannot exceed page size');
    }

    const usedWidth =
      values.margin_left +
      values.columns * values.label_width +
      (values.columns - 1) * values.gutter_horizontal;
    if (usedWidth > values.page_width) {
      throw new Error('Labels and gutters exceed page width');
    }

    const usedHeight =
      values.margin_top +
      values.rows * values.label_height +
      (values.rows - 1) * values.gutter_vertical;
    if (usedHeight > values.page_height) {
      throw new Error('Labels and gutters exceed page height');
    }
  }

  private sanitize(values: SheetFormatUpsertValues): SheetFormatUpsertValues {
    return {
      ...values,
      name: values.name.trim(),
      product_code: values.product_code.trim(),
      manufacturer: values.manufacturer.trim(),
    };
  }

  /** Create a custom sheet format (is_system = 0). */
  async create(values: SheetFormatUpsertValues): Promise<SheetFormat> {
    await this.db.init();
    const sanitized = this.sanitize(values);
    this.validate(sanitized);

    const id = this.db.generateId();
    const now = Date.now();

    await this.db.run(
      `INSERT INTO sheet_formats (
        id, name, product_code, manufacturer,
        page_width, page_height, label_width, label_height,
        columns, rows, margin_top, margin_left,
        gutter_horizontal, gutter_vertical, corner_radius,
        is_system, created_by, created_at
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 0, '', ?)`,
      [
        id,
        sanitized.name,
        sanitized.product_code,
        sanitized.manufacturer,
        sanitized.page_width,
        sanitized.page_height,
        sanitized.label_width,
        sanitized.label_height,
        sanitized.columns,
        sanitized.rows,
        sanitized.margin_top,
        sanitized.margin_left,
        sanitized.gutter_horizontal,
        sanitized.gutter_vertical,
        sanitized.corner_radius,
        now,
      ],
    );

    const created = await this.getById(id);
    if (!created) {
      throw new Error('Failed to load sheet format after create');
    }
    this.logger.info('Created custom sheet format', { id: created.id, name: created.name });
    return created;
  }

  /** Update a custom sheet format. System formats are immutable. */
  async update(params: UpdateSheetFormatParams): Promise<SheetFormat> {
    await this.db.init();
    const existing = await this.getById(params.id);
    if (!existing) {
      throw new Error('Sheet format not found');
    }
    if (existing.is_system === 1) {
      throw new Error('System sheet formats cannot be edited');
    }

    const sanitized = this.sanitize(params.values);
    this.validate(sanitized);

    await this.db.run(
      `UPDATE sheet_formats
       SET name = ?, product_code = ?, manufacturer = ?,
           page_width = ?, page_height = ?, label_width = ?, label_height = ?,
           columns = ?, rows = ?, margin_top = ?, margin_left = ?,
           gutter_horizontal = ?, gutter_vertical = ?, corner_radius = ?
       WHERE id = ?`,
      [
        sanitized.name,
        sanitized.product_code,
        sanitized.manufacturer,
        sanitized.page_width,
        sanitized.page_height,
        sanitized.label_width,
        sanitized.label_height,
        sanitized.columns,
        sanitized.rows,
        sanitized.margin_top,
        sanitized.margin_left,
        sanitized.gutter_horizontal,
        sanitized.gutter_vertical,
        sanitized.corner_radius,
        params.id,
      ],
    );

    const updated = await this.getById(params.id);
    if (!updated) {
      throw new Error('Failed to load sheet format after update');
    }
    this.logger.info('Updated custom sheet format', { id: updated.id, name: updated.name });
    return updated;
  }

  /** Hard-delete a custom sheet format. System formats cannot be deleted. */
  async delete(id: string): Promise<void> {
    await this.db.init();
    const existing = await this.getById(id);
    if (!existing) {
      throw new Error('Sheet format not found');
    }
    if (existing.is_system === 1) {
      throw new Error('System sheet formats cannot be deleted');
    }

    await this.db.run('DELETE FROM sheet_formats WHERE id = ?', [id]);
    this.logger.info('Deleted custom sheet format', { id });
  }
}
